import { get, off, onValue, ref, remove, set, update } from "firebase/database";
import { db } from "../lib/firebase";
import { getPathDatabaseByRole } from "./auth";
import { LaunchError } from "../utils/errors";
import { buildCompetitorAdFavoritePayload } from "../services/metaAdLibrary";

const getCompetitorAdFavoritesPath = ({ advertiserID }) =>
  `advertisersCompetitors/${advertiserID}/favorite`;

const getCompetitorAdFavoriteItemPath = ({ advertiserID, pageId, libraryID }) =>
  `${getCompetitorAdFavoritesPath({ advertiserID })}/${pageId}/${libraryID}`;

const getCompetitorAdCacheItemPath = ({ advertiserID, pageId, libraryID }) =>
  `advertisersCompetitors/${advertiserID}/${pageId}/ads/${libraryID}`;

export const startListenerCompetitorAdFavorites =
  ({ onUpdatedDataCallback = () => {}, onReferenceAvailable = () => {} }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());

      if (!userID) {
        onUpdatedDataCallback(null);
        return false;
      }

      const dbRef = ref(
        db,
        getCompetitorAdFavoritesPath({ advertiserID: userID })
      );
      onReferenceAvailable(dbRef);
      onValue(dbRef, (snapshot) => {
        onUpdatedDataCallback(snapshot.val() || null);
      });

      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerCompetitorAdFavorites",
          tags: { action: "competitorAdFavorites" },
          level: "error",
        })
      );
      return false;
    }
  };

export const stopListenerCompetitorAdFavorites =
  ({ listenerRef } = {}) =>
  async () => {
    if (!listenerRef) {
      return;
    }

    await off(listenerRef);
  };

export const startSetCompetitorAdFavorite =
  ({ pageId, libraryID, ad }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());

      if (!userID || !pageId || !libraryID || !ad?.url) {
        return { ok: false };
      }

      const payload = buildCompetitorAdFavoritePayload({
        pageId,
        libraryID,
        ad,
      });
      const favoriteRef = ref(
        db,
        getCompetitorAdFavoriteItemPath({
          advertiserID: userID,
          pageId,
          libraryID,
        })
      );
      const cacheRef = ref(
        db,
        getCompetitorAdCacheItemPath({
          advertiserID: userID,
          pageId,
          libraryID,
        })
      );

      await Promise.all([
        set(favoriteRef, payload),
        update(cacheRef, { isFavorite: true }),
      ]);

      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startSetCompetitorAdFavorite",
          context: { pageId, libraryID },
          tags: { action: "competitorAdFavorites" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startRemoveCompetitorAdFavorite =
  ({ pageId, libraryID }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());

      if (!userID || !pageId || !libraryID) {
        return { ok: false };
      }

      const favoriteRef = ref(
        db,
        getCompetitorAdFavoriteItemPath({
          advertiserID: userID,
          pageId,
          libraryID,
        })
      );
      const cacheRef = ref(
        db,
        getCompetitorAdCacheItemPath({
          advertiserID: userID,
          pageId,
          libraryID,
        })
      );

      await Promise.all([
        remove(favoriteRef),
        update(cacheRef, { isFavorite: null }),
      ]);

      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startRemoveCompetitorAdFavorite",
          context: { pageId, libraryID },
          tags: { action: "competitorAdFavorites" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startGetCompetitorAdFavorites = () => async (dispatch) => {
  try {
    const { userID } = dispatch(getPathDatabaseByRole());

    if (!userID) {
      return { ok: false, data: null };
    }

    const snapshot = await get(
      ref(db, getCompetitorAdFavoritesPath({ advertiserID: userID }))
    );

    return {
      ok: true,
      data: snapshot.val() || null,
    };
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startGetCompetitorAdFavorites",
        tags: { action: "competitorAdFavorites" },
        level: "error",
      })
    );
    return { ok: false };
  }
};
